import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTruck,
  faTag,
  faComments,
  faBuilding,
  faEnvelope,
  faArrowRight,
  faBoxesStacked,
  faMoneyBillWave,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontTestimonials,
  placeholderImage,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { WholesaleEnquiry, wholesaleMinimum } from '../components/WholesaleEnquiry'
import { PoliniMinisetSpotlight } from '../components/PoliniMinisetSpotlight'
import { BRANDS, getBrandLandingHref } from '../data/brands'
import type { Brand } from '../data/brands'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const
const CATEGORY_IMAGE_TRANSFORM = { width: 360, height: 360, smart: true, format: 'webp', quality: 60 } as const

const LP_SOURCE = 'landing_page' as const
// Used as lp_category on leads so the hub page's conversions are distinguishable
// from the per-category LPs in GA4.
const PAGE_NAME = 'Pusat Grosir'

const HERO_CATEGORY_SHORTCUTS = [
  { label: 'Kaos kaki', href: '#grosir-kaos-kaki' },
  { label: 'Handuk', href: '#grosir-handuk' },
  { label: 'Bra & lingerie', href: '#grosir-bra-lingerie' },
  { label: 'CD & underwear', href: '#grosir-cd-underwear' },
  { label: 'Sarung tangan', href: '#grosir-sarung-tangan' },
  { label: 'Piyama', href: '#grosir-piyama-oneset' },
] as const

export function HeroCategoryShortcuts() {
  return (
    <nav aria-label="Kategori grosir utama" className="mt-5">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Pilih kategori yang dicari</p>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
        {HERO_CATEGORY_SHORTCUTS.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={() => track('lp_browse_catalog', { cta_placement: 'hero_category_shortcut', lp_category: PAGE_NAME, destination: item.href })}
            className="inline-flex min-h-11 shrink-0 items-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:border-amber-300/70 hover:bg-white/15"
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  )
}

// An extra static card for the standalone gloves LP, which isn't a storefront
// category but should still appear in the wholesale hub.
const EXTRA_CATEGORY_CARD = {
  name: 'Sarung Tangan',
  href: '/lp/grosir-sarung-tangan',
  image: '/lp/sarung-tangan-gekko-3.jpeg',
}

const FEATURED_BRAND_SLUGS = ['piaoli', 'polini', 'tally', 'lupo', 'comet', 'gekko', 'winteku', 'leisure'] as const

const BRAND_CATEGORY_LABELS: Record<string, string> = {
  'bra-lingerie': 'Bra & lingerie',
  'camisol-longtorso': 'Camisole & longtorso',
  'cd-underwear': 'CD & underwear',
  handuk: 'Handuk',
  'kaos-kaki': 'Kaos kaki',
  'manshet-sarung-tangan': 'Manset & sarung tangan',
  masker: 'Masker',
  'piyama-oneset': 'Piyama & oneset',
}

const FEATURED_BRAND_STYLES = [
  { badge: 'bg-rose-500', glow: 'from-rose-100' },
  { badge: 'bg-violet-600', glow: 'from-violet-100' },
  { badge: 'bg-cyan-600', glow: 'from-cyan-100' },
  { badge: 'bg-orange-500', glow: 'from-orange-100' },
  { badge: 'bg-blue-700', glow: 'from-blue-100' },
  { badge: 'bg-emerald-700', glow: 'from-emerald-100' },
  { badge: 'bg-amber-500', glow: 'from-amber-100' },
  { badge: 'bg-fuchsia-600', glow: 'from-fuchsia-100' },
] as const

const FEATURED_BRANDS = FEATURED_BRAND_SLUGS
  .map((slug) => BRANDS.find((brand) => brand.slug === slug))
  .filter((brand): brand is Brand => Boolean(brand))

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// The testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Kategori grosir apa saja yang tersedia?',
    a: 'Dropify menyediakan kaos kaki, manset dan sarung tangan, pakaian dalam pria serta wanita, piyama, oneset, camisole, longtorso, handuk, dan kategori kebutuhan harian lain. Kirim kategori dan jumlah agar admin dapat memeriksa stok serta harga.',
  },
  {
    q: 'Mencari pusat grosir terdekat?',
    a: 'Dropify melayani pemesanan online melalui WhatsApp dan mengirim ke seluruh Indonesia, termasuk Bandung, Jakarta, Surabaya, Solo, dan Jogja. Sampaikan kategori serta kota tujuan untuk menerima estimasi ongkos kirim sebelum memesan.',
  },
  {
    q: 'Minimal ordernya berapa sih?',
    a: 'Minimum order berbeda per kategori. Kirim produk dan jumlah yang dibutuhkan agar admin dapat menjelaskan MOQ serta menyiapkan quotation.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Bagaimana cara meminta harga grosir?',
    a: 'Harga mengikuti produk dan jumlah pesanan. Minta quotation, lalu hitung margin setelah memasukkan ongkir, kemasan, biaya platform, promosi, dan retur.',
  },
  {
    q: 'Bisa campur jenis barang dalam satu order?',
    a: 'Bisa. Beberapa kategori dapat disatukan dalam satu pengiriman. MOQ tetap diperiksa per kategori dan admin akan merinci susunannya pada quotation.',
  },
  {
    q: 'Apakah saya dapat meminta sample?',
    a: 'Ketersediaan sampel perlu diperiksa per produk. Total biaya sampel terdiri dari harga satuan produk dan ongkir ke kota tujuan.',
  },
  {
    q: 'Bagaimana proses pembayarannya?',
    a: 'Pembayaran dilakukan melalui transfer BCA atau Mandiri. Admin memeriksa bukti transfer dan mengonfirmasi pembayaran sebelum pesanan disiapkan.',
  },
  {
    q: 'Pengirimannya berapa lama?',
    a: 'Admin memeriksa waktu penyiapan setelah produk, jumlah, dan pembayaran dikonfirmasi. Estimasi perjalanan mengikuti kota tujuan serta layanan ekspedisi. Nomor resi dikirim setelah paket diserahkan kepada ekspedisi.',
  },
  {
    q: 'Saya baru akan mulai menjual. Informasi apa yang perlu saya kirim?',
    a: 'Kirim kategori, target pembeli, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan produk, MOQ, quotation, serta pengiriman.',
  },
]

export const Route = createFileRoute('/pusat-grosir')({
  loader: async () => {
    const [config, categories, testimonialsRaw, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => []),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      categories,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Pusat Grosir Sandang untuk Toko dan Reseller')
    const description =
      'Kulakan kaos kaki, manset, sarung tangan, pakaian dalam, bra, dan piyama. Pilih kategori lalu minta harga, cek stok, serta ongkir melalui WhatsApp.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/pusat-grosir') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/pusat-grosir') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: PusatGrosirPage,
})

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative sm:whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

// The four questions buyers actually open in the FAQ (GA4 faq_open, Jun–Aug
// 2026), answered above the fold instead of in the bottom accordion. Facts
// mirror /minimum-order and the FAQ answers below.
type DoubtItem = {
  icon: typeof faTag
  title: string
  desc: string
  link?: { href: string; label: string }
  payments?: boolean
}

const DOUBT_ITEMS: DoubtItem[] = [
  {
    icon: faBoxesStacked,
    title: 'Minimal order 10–50 lusin',
    desc: 'Beda per kategori: handuk mulai 10 lusin, bra & sarung tangan 25 lusin, kaos kaki & CD 50 lusin.',
    link: { href: '/minimum-order', label: 'Detail per kategori →' },
  },
  {
    icon: faTag,
    title: 'Quotation sebelum pembayaran',
    desc: 'Admin merinci produk, jumlah, harga, dan ongkir sebelum Anda melakukan pembayaran.',
  },
  {
    icon: faMoneyBillWave,
    title: 'Bayar transfer BCA/Mandiri',
    desc: 'Order dikonfirmasi 1×24 jam kerja begitu pembayaran masuk.',
    payments: true,
  },
  {
    icon: faTruck,
    title: 'Kirim se-Indonesia',
    desc: 'Waktu penyiapan dikonfirmasi oleh admin. Estimasi perjalanan mengikuti kota tujuan dan ekspedisi. Resi dikirim setelah paket berangkat.',
  },
]

const FIT_ITEMS = [
  'Punya toko atau warung dan ingin menambah kategori produk grosir.',
  'Reseller yang ingin membeli stok sesuai MOQ kategori.',
  'Penjual baru yang memerlukan informasi produk, satuan, dan cara order.',
  'Toko yang menerima permintaan dalam jumlah besar dan perlu memastikan stok.',
  'Pembeli yang ingin menerima quotation sebelum menghitung margin penjualan.',
  'Toko yang perlu mencocokkan barang dengan kode produk dan foto stok.',
]

const STEPS = [
  {
    icon: faComments,
    title: 'Chat admin',
    desc: 'Klik tombol WhatsApp, lalu kirim kategori, produk, jumlah, dan kota tujuan.',
  },
  {
    icon: faMoneyBillWave,
    title: 'Periksa quotation dan bayar',
    desc: 'Admin mengirim rincian harga dan ongkir. Setelah Anda menyetujuinya, pembayaran dilakukan melalui transfer BCA atau Mandiri.',
  },
  {
    icon: faTruck,
    title: 'Barang dikirim',
    desc: 'Admin mengonfirmasi waktu penyiapan. Nomor resi dikirim setelah paket diserahkan kepada ekspedisi.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  // When the CTA is tied to a specific category, pass its name so the lead
  // carries which item drove it (mirrors the per-category LP tracking).
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps(LP_SOURCE, {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PAGE_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={`${className ?? ''} max-w-full max-[360px]:px-3 max-[360px]:text-center`}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function PusatGrosirPage() {
  const { config, categories, testimonialImages, newsPosts } = Route.useLoaderData()

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya ingin membeli barang untuk dijual kembali. Kategori: ___. Perkiraan jumlah: ___. Kota tujuan: ___. Mohon periksa pilihan produk, stok, quotation, dan ongkir.'
  const ctaMessage = 'Halo Dropify, saya mau kulakan untuk dijual lagi. Kategori: ___. Perkiraan jumlah: ___. Kota tujuan: ___. Bisa bantu siapkan pilihan produk dan harganya?'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Pemesanan grosir melalui WhatsApp. Harga dan stok dikonfirmasi sebelum order.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3 max-[360px]:h-auto max-[360px]:flex-wrap max-[360px]:py-3">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Hubungi admin"
            placement="header"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors max-[360px]:w-full"
          />
        </div>
      </header>

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-10 md:py-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              Buat reseller & toko
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Pusat grosir sandang untuk <Mark>toko dan reseller.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Kaos kaki, handuk, manset, pakaian dalam, bra, piyama, dan sarung tangan kerja untuk stok toko atau pengadaan. Pilih kategori untuk memeriksa MOQ dan meminta harga.
            </p>

            <HeroCategoryShortcuts />

          </div>

          <WholesaleEnquiry />
        </div>
      </section>

      {/* Category grid directly under the hero — GA4 (Jun–Aug 2026): category
          selection is the page's dominant action (65 lp_browse_catalog clicks vs
          42 on-page WA clicks) and 37% of this page's leads convert after
          navigating to a category/product page. */}
      <CategoryGrid categories={categories} />

      {/* Pre-order facts — the four questions buyers actually open in the FAQ
          (faq_open: harga 7, MOQ 7, bayar 6, kirim 4), answered where ~95% of
          visitors still are instead of in the accordion at 90% scroll depth. */}
      <DoubtStrip />
      <BrandHighlights />
      <PoliniMinisetSpotlight />

      <AdNewsProofSection posts={newsPosts} />

      {/* Brand-protection notice — Dropify only takes orders via WhatsApp, not marketplaces */}
      <BrandNotice />

      {/* Who it's for — let the visitor self-identify */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pembeli yang dilayani halaman ini</h2>
            <p className="mt-2 text-base text-slate-500">Layanan ini ditujukan untuk pembelian grosir. Cocokkan kategori dan ketentuannya dengan kebutuhan usaha Anda.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4 max-[360px]:flex-col">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cara memesan melalui WhatsApp</h2>
            <p className="mt-2 text-base text-slate-500">Pemesanan, quotation, dan konfirmasi pembayaran dilakukan melalui percakapan dengan admin.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {STEPS.map((step, i) => (
              <div key={step.title} className="relative rounded-2xl border border-slate-100 bg-white p-6">
                <span className="absolute right-5 top-5 text-4xl font-black text-slate-100">{i + 1}</span>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-700">
                  <FontAwesomeIcon icon={step.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <WhatsAppButton
              message={heroMessage}
              label="Kirim daftar kebutuhan"
              placement="proof_section"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/20 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* First-hand supplier positioning — matches the highest-impression
          search query "supplier tangan pertama langsung dari pabrik" */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
            Distributor grosir dengan pemesanan melalui WhatsApp
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">
            Dropify melayani pemesanan grosir dari Surabaya melalui WhatsApp dan mengirim pesanan ke berbagai kota di Indonesia.
            Harga dikonfirmasi melalui quotation sesuai produk, jumlah, serta kota tujuan sebelum pembayaran.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Kirim daftar kebutuhan grosir</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Kirim kategori, jumlah, dan kota tujuan. Admin akan memeriksa produk serta menyiapkan quotation sebelum Anda memesan.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation via WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Harga dan stok mengikuti produk serta jumlah yang dikonfirmasi admin.
          </p>
        </div>
      </section>

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/pusat-grosir" />

      {/* Footer with legitimacy + contact (no nav links — keeps the ad funnel tight) */}
      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pemesanan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps(LP_SOURCE, { cta_placement: 'footer_phone', lp_category: PAGE_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

export function BrandHighlights() {
  return (
    <section className="overflow-hidden border-b border-slate-800 bg-[#0B1120] py-14 text-white">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-300">Cari berdasarkan merek</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Merek yang tersedia untuk kebutuhan kulakan</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
              Buka halaman merek untuk melihat lini produk yang tersedia, lalu pilih kategori atau model yang sesuai. Gunakan nomor model saat meminta stok agar admin dapat mencocokkan barang.
            </p>
          </div>
          <a
            href="/merek"
            onClick={() => track('brand_directory_click', { cta_placement: 'brand_section_header', lp_category: PAGE_NAME, destination: '/merek' })}
            className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            Buka direktori merek
            <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURED_BRANDS.map((brand, index) => {
            const href = getBrandLandingHref(brand)
            const style = FEATURED_BRAND_STYLES[index]
            const categories = brand.categorySlugs.map((slug) => BRAND_CATEGORY_LABELS[slug] ?? slug).slice(0, 3)

            return (
              <a
                key={brand.slug}
                href={href}
                onClick={() => track('brand_directory_click', { cta_placement: 'featured_brand', lp_category: PAGE_NAME, brand_name: brand.name, destination: href })}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${style.glow} via-white to-white p-5 text-slate-900 transition-all hover:-translate-y-0.5 hover:border-white/30 hover:shadow-xl hover:shadow-black/20`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${style.badge} text-base font-black text-white shadow-sm`}>
                    {brand.name.slice(0, 2).toUpperCase()}
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} className="mt-1 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-slate-900" />
                </div>
                <h3 className="mt-5 text-xl font-black tracking-tight">{brand.name}</h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{categories.join(' · ')}</p>
                <p className="mt-4 text-sm font-bold text-blue-700">Lihat katalog {brand.name}</p>
              </a>
            )
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.04] p-5 md:p-6">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <h3 className="text-lg font-black tracking-tight">Semua merek di katalog Dropify</h3>
            <p className="text-sm text-slate-400">{BRANDS.length} halaman merek</p>
          </div>
          <nav aria-label="Daftar merek Dropify" className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
            {BRANDS.map((brand) => {
              const href = getBrandLandingHref(brand)
              return (
                <a
                  key={brand.slug}
                  href={href}
                  onClick={() => track('brand_directory_click', { cta_placement: 'all_brands', lp_category: PAGE_NAME, brand_name: brand.name, destination: href })}
                  className="inline-flex min-h-11 items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-300/50 hover:bg-white/10 hover:text-white"
                >
                  <span>{brand.name}</span>
                  <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3 flex-shrink-0 text-slate-500" />
                </a>
              )
            })}
          </nav>
        </div>
      </div>
    </section>
  )
}


function DoubtStrip() {
  return (
    <section className="py-10 bg-slate-50 border-y border-slate-100">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Informasi yang perlu diperiksa sebelum order</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {DOUBT_ITEMS.map((item) => (
            <div key={item.title} className="rounded-2xl border border-slate-100 bg-white p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600">
                <FontAwesomeIcon icon={item.icon} className="h-4.5 w-4.5 text-white" />
              </div>
              <h3 className="mt-3.5 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              {item.link && (
                <a href={item.link.href} className="mt-2 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700">
                  {item.link.label}
                </a>
              )}
              {item.payments && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="flex items-center justify-center rounded-md border border-slate-200 bg-white px-2 py-1">
                    <img src="/bca.svg" alt="BCA" width="48" height="15" loading="lazy" decoding="async" className="h-3.5 w-auto object-contain" />
                  </span>
                  <span className="flex items-center justify-center rounded-md border border-slate-200 bg-white px-2 py-1">
                    <img src="/mandiri.png" alt="Mandiri" width="54" height="15" loading="lazy" decoding="async" className="h-3.5 w-auto object-contain" />
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function BrandNotice() {
  return (
    <section className="bg-white pt-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
          <p className="text-base leading-relaxed text-slate-700">
            <span className="font-bold text-slate-900">Pemesanan resmi Dropify dilakukan melalui WhatsApp.</span> Dropify tidak membuka toko di Tokopedia, Shopee, Lazada, atau marketplace lain. Pastikan nomor tujuan sesuai dengan kontak yang tercantum di website ini.
          </p>
        </div>
      </div>
    </section>
  )
}

function CategoryGrid({ categories }: { categories: StorefrontCategory[] }) {
  const cards = [
    ...categories.map((category) => ({
      name: category.name,
      slug: category.slug,
      categoryHref: `/kategori/${category.slug}`,
      image: toExternalImage(category.image_url, CATEGORY_IMAGE_TRANSFORM) || placeholderImage(category.name, 360, 360),
      price: undefined,
    })),
    ...HERO_CATEGORY_SHORTCUTS
      .map((item) => ({ name: item.label, slug: item.href.replace('#grosir-', '') }))
      .filter((item) => item.slug !== 'sarung-tangan' && !categories.some((category) => category.slug === item.slug))
      .map((item) => ({
        ...item, categoryHref: `/kategori/${item.slug}`,
        image: placeholderImage(item.name, 360, 360), price: undefined,
      })),
    {
      name: EXTRA_CATEGORY_CARD.name,
      slug: 'sarung-tangan',
      // The gloves card isn't a storefront category, so it has no /kategori page —
      // its "category" destination is its standalone landing page.
      categoryHref: EXTRA_CATEGORY_CARD.href,
      image: EXTRA_CATEGORY_CARD.image,
      price: undefined,
    },
  ]

  return (
    <section id="kategori-grosir" className="scroll-mt-6 py-14 bg-white border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Mau kulakan apa?</h2>
          <p className="mt-2 text-base text-slate-500">Pilih kategori untuk melihat produk dan cara order. Jika barang belum tampil, kirim nama atau foto referensinya kepada admin.</p>
        </div>
        <nav aria-label="Pilih kategori grosir" className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-2 lg:hidden">
          {cards.slice(0, 5).map((card) => (
            <a key={card.categoryHref} href={card.categoryHref}
              onClick={() => track('lp_browse_catalog', { cta_placement: 'mobile_category_shortcut', lp_category: PAGE_NAME, destination: card.categoryHref })}
              className="inline-flex min-h-11 shrink-0 items-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700">
              {card.name}
            </a>
          ))}
        </nav>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {cards.map((card) => (
            <div
              key={card.categoryHref}
              id={`grosir-${card.slug}`}
              className="scroll-mt-4 group relative flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg"
            >
              <a href={card.categoryHref} className="relative block overflow-hidden bg-slate-50">
                <img src={card.image} alt={`Grosir ${card.name}`} width="360" height="360" loading="lazy" decoding="async" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </a>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="text-sm font-black tracking-tight text-slate-900">Grosir {card.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">{wholesaleMinimum(card.slug)}</p>
                <div className="mt-3 flex flex-1 flex-col justify-end gap-2">
                  <a
                    href={waLink(`Halo Dropify, saya mau kulakan ${card.name}. Produk atau model: ___. Kebutuhan sekitar ___ lusin, kirim ke ___. Bisa cek MOQ, stok, dan quotation?`)}
                    {...whatsAppCtaProps(LP_SOURCE, { cta_placement: 'category_card', lp_category: PAGE_NAME, lead_item_name: card.name })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg bg-green-700 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-green-800"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5" /> Cek stok & harga
                  </a>
                  <a
                    href={card.categoryHref}
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:border-blue-200 hover:text-blue-600"
                  >
                    Lihat model <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* No-chat path: let cautious visitors browse the whole catalog without messaging */}
        <div className="mt-8 text-center">
          <a
            href="/katalog"
            onClick={() => track('lp_browse_catalog', { lp_category: PAGE_NAME })}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-6 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
          >
            Lihat Katalog Lengkap
            <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
          </a>
          <p className="mt-2 text-xs text-slate-400">Anda dapat membuka katalog sebelum menghubungi admin.</p>
        </div>
      </div>
    </section>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan yang telah ditangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar pemesanan grosir</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'pusat-grosir' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
